import { ICompany } from "../../../customTypings/company/Company";
import { ErrorMessage, Field, Formik, Form as FormikForm } from "formik";
import { useEffect, useState } from "react";
import {
    Alert, Button, Col, Container, Form, Row
} from "react-bootstrap";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { companyService } from "../../../services";
import { LoadingSpinner } from "../../common/Loading/LoadingSpinner";
import { ResponsiveBreadcrumb } from "../../Layout/ResponsiveBreadcrumb";
import { AddressFields, AddressLookupProvider, PostcodeLookup } from "../../UI";

const emptyCompany: ICompany = {
    id: "",
    phone: "",
    contactEmail: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    website: "",
    invitationEmail: "",
};

const companySchema = Yup.object().shape({
    phone: Yup.string()
        .required("Required")
        .matches(
            /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
            "Invalid phone number"
        ),
    contactEmail: Yup.string().required("Required").email("Invalid email address"),
    addressLine1: Yup.string().required("Required"),
    addressLine2: Yup.string().optional(),
    addressLine3: Yup.string().optional(),
    town: Yup.string().required("Required"),
    county: Yup.string().optional(),
    postcode: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    website: Yup.string()
        .required("Required")
        .matches(
            /^(http[s]?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}(\/[a-zA-Z0-9+&@#/%?=~_|!:,.;-]*)?$/,
            "Invalid Website"
        ),
    invitationEmail: Yup.string().required("Required").email("Invalid email address"),
});

function CompanyInfo() {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [currentCompany, setCurrentCompany] = useState<ICompany>(emptyCompany);

    useEffect(() => {
        companyService.getCompany().then((res) => {
            if (res) {
                setCurrentCompany({
                    ...emptyCompany,
                    ...res,
                    country: res.country ?? "United Kingdom",
                });
            }
        });
    }, []);

    const save = (
        values: ICompany,
        setStatus: (status: string) => void,
        setSubmitting: (submitting: boolean) => void
    ) => {
        const updatedCompany: ICompany = {
            id: currentCompany.id ? currentCompany.id : undefined,
            phone: values.phone,
            contactEmail: values.contactEmail,
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            addressLine3: values.addressLine3,
            town: values.town,
            county: values.county,
            postcode: values.postcode,
            country: values.country,
            website: values.website,
            invitationEmail: values.invitationEmail,
        };
        const serviceMethod = currentCompany.id ? companyService.update : companyService.create;
        const updateMessage = currentCompany.id
            ? "Company Info has been updated successfully"
            : "Company Info has been created successfully";

        serviceMethod(updatedCompany).then(
            (response) => {
                setSubmitting(false);
                if (response.status === "Success") {
                    setAlertVariant("success");
                    setStatus(updateMessage);
                } else {
                    setAlertVariant("danger");
                    setStatus(response.message);
                }
            },
            (error: { title?: string }) => {
                setAlertVariant("danger");
                setStatus(error.title ?? "Save failed.");
                setSubmitting(false);
            }
        );
    };

    return (
        <Container fluid>
            <Helmet>
                <title>Company info</title>
            </Helmet>
            <ResponsiveBreadcrumb
                items={[
                    { text: "Settings", to: "/settings" },
                    { text: "Company info" },
                ]}
            />
            <h1 className="my-3">Company Info</h1>
            <Formik
                initialValues={currentCompany}
                onSubmit={(values, { setStatus, setSubmitting }) => {
                    save(values, setStatus, setSubmitting);
                }}
                validationSchema={companySchema}
                enableReinitialize
            >
                {({
                    errors,
                    status,
                    touched,
                    isSubmitting,
                    handleSubmit,
                    values,
                    handleChange,
                    setFieldValue,
                    setFieldError,
                    setFieldTouched,
                    setStatus,
                }) => (
                    <FormikForm onSubmit={handleSubmit}>
                        <Form.Group as={Row} className="mb-3" controlId="phone">
                            <Form.Label column sm={3}>
                                Phone <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <Col sm={9}>
                                <Field
                                    name="phone"
                                    type="tel"
                                    className={`form-control ${errors.phone && touched.phone ? "is-invalid" : ""}`}
                                />
                                <ErrorMessage name="phone" component="div" className="invalid-feedback" />
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3" controlId="contactEmail">
                            <Form.Label column sm={3}>
                                Contact Email <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <Col sm={9}>
                                <Field
                                    name="contactEmail"
                                    type="email"
                                    className={`form-control ${errors.contactEmail && touched.contactEmail ? "is-invalid" : ""}`}
                                />
                                <ErrorMessage name="contactEmail" component="div" className="invalid-feedback" />
                            </Col>
                        </Form.Group>

                        <AddressLookupProvider>
                            <PostcodeLookup
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                setFieldError={setFieldError}
                                setFieldTouched={setFieldTouched}
                                setStatus={setStatus}
                                required
                            />
                            <AddressFields
                                values={values}
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                showPostcode={false}
                                showLine3
                                requiredFields={["addressLine1", "town", "country"]}
                            />
                        </AddressLookupProvider>

                        <Form.Group as={Row} className="mb-3" controlId="website">
                            <Form.Label column sm={3}>
                                Website <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <Col sm={9}>
                                <Field
                                    name="website"
                                    className={`form-control ${errors.website && touched.website ? "is-invalid" : ""}`}
                                />
                                <ErrorMessage name="website" component="div" className="invalid-feedback" />
                            </Col>
                        </Form.Group>

                        <Form.Group as={Row} className="mb-3" controlId="invitationEmail">
                            <Form.Label column sm={3}>
                                Invitation Email <span style={{ color: "red" }}>*</span>
                            </Form.Label>
                            <Col sm={9}>
                                <Field
                                    name="invitationEmail"
                                    type="email"
                                    className={`form-control ${errors.invitationEmail && touched.invitationEmail ? "is-invalid" : ""}`}
                                />
                                <ErrorMessage name="invitationEmail" component="div" className="invalid-feedback" />
                            </Col>
                        </Form.Group>

                        <Button
                            variant="primary"
                            disabled={isSubmitting}
                            type="submit"
                            className="me-2 d-flex align-items-end ms-auto"
                        >
                            {isSubmitting ? (
                                <LoadingSpinner
                                    text={currentCompany.id ? "Updating... " : "Saving ..."}
                                />
                            ) : (
                                "Save"
                            )}
                        </Button>

                        {status && (
                            <Alert variant={alertVariant} className="mt-3">
                                {status}
                            </Alert>
                        )}
                    </FormikForm>
                )}
            </Formik>
        </Container>
    );
}

export default CompanyInfo;
